import { z } from 'zod';
import { idSchema, isoDateTimeSchema } from './common';
import { roleSchema, userPublicSchema, userStatusSchema } from './users';

export const inviteStatusSchema = z.enum(['unused', 'used', 'expired', 'revoked']);
export type InviteStatus = z.infer<typeof inviteStatusSchema>;

/** Who created an invite (any member can, DESIGN 4.16). */
export const inviteCreatorSchema = z.object({ id: idSchema, displayName: z.string() });
export type InviteCreator = z.infer<typeof inviteCreatorSchema>;

/**
 * An account invite, as listed to the admin (`GET /api/admin/invites`, every invite) and to
 * its creator (`GET /api/invites`, their own).
 */
export const adminInviteSchema = z.object({
  id: idSchema,
  code: z.string(),
  /** `https://split.alazaro.es/invite/<code>` (built from the request origin). */
  url: z.string(),
  note: z.string().nullable(),
  status: inviteStatusSchema,
  createdAt: isoDateTimeSchema,
  expiresAt: isoDateTimeSchema.nullable(),
  usedAt: isoDateTimeSchema.nullable(),
  usedBy: userPublicSchema.nullable(),
  revokedAt: isoDateTimeSchema.nullable(),
  /** `null` when the creating account no longer exists (legacy rows). */
  createdBy: inviteCreatorSchema.nullable(),
});
export type AdminInvite = z.infer<typeof adminInviteSchema>;
/** Same item for both lists; `Invite` is the name used outside the admin area. */
export const inviteSchema = adminInviteSchema;
export type Invite = AdminInvite;

/** Sorted: unused first, then by creation date descending. */
export const adminInviteListSchema = z.object({ items: z.array(adminInviteSchema) });
export type AdminInviteList = z.infer<typeof adminInviteListSchema>;

/**
 * `GET /api/invites`: the caller's own invites, sorted like the admin list (unused first,
 * then by creation date descending).
 */
export const myInviteListSchema = z.object({ invites: z.array(inviteSchema) });
export type MyInviteList = z.infer<typeof myInviteListSchema>;

/**
 * Invites a non-admin may create per rolling 24 hours through `POST /api/invites` (then
 * `429 RATE_LIMITED` with `Retry-After`). Admins are not limited.
 */
export const INVITES_PER_DAY_LIMIT = 20;

/**
 * `POST /api/invites` and `POST /api/admin/invites`. `expiresInHours: null` means never;
 * omitted means 7 days.
 */
export const createInviteRequestSchema = z.object({
  note: z.string().trim().max(60).optional(),
  /** The UI offers 24 (24 h), 168 (7 days, default), 720 (30 days) or null (never). */
  expiresInHours: z
    .int()
    .min(1)
    .max(24 * 365)
    .nullable()
    .optional(),
});
export type CreateInviteRequest = z.input<typeof createInviteRequestSchema>;

export const adminUserSchema = userPublicSchema.extend({
  role: roleSchema,
  status: userStatusSchema,
  createdAt: isoDateTimeSchema,
  lastActiveAt: isoDateTimeSchema.nullable(),
  groupCount: z.int(),
});
export type AdminUser = z.infer<typeof adminUserSchema>;
export const adminUserListSchema = z.object({ items: z.array(adminUserSchema) });
export type AdminUserList = z.infer<typeof adminUserListSchema>;
